import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { api } from "../api";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function safeReaderDestination(
  value
) {
  const destination =
    clean(value);

  return /^\/reader\/screenplay\/[a-fA-F0-9]{24}$/
    .test(destination)
      ? destination
      : "";
}

function languageLabel(
  value
) {
  const language =
    clean(value)
      .toLowerCase();

  if (
    language.startsWith(
      "fr"
    )
  ) {
    return "Français";
  }

  if (
    language.startsWith(
      "en"
    )
  ) {
    return "English";
  }

  return "";
}

function visibilityLabel(
  value,
  isFR
) {
  return clean(value) ===
    "nextscenes_readers"
      ? (
          isFR
            ? "Lecteurs NextScenes"
            : "NextScenes readers"
        )
      : "Public";
}

export default function ScreenplayDiscovery() {
  const lang =
    (
      typeof window !==
        "undefined" &&
      window.localStorage
        .getItem("ns.lang")
    ) ||
    "en";

  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const [
    items,
    setItems,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const loadScreenplays =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const response =
            await api
              .getScreenplayDiscovery(
                24
              );

          setItems(
            Array.isArray(
              response?.items
            )
              ? response.items
              : []
          );
        } catch (_error) {
          setItems([]);

          setError(
            isFR
              ? "Les scénarios ne peuvent pas être chargés pour le moment."
              : "Screenplays could not be loaded right now."
          );
        } finally {
          setLoading(false);
        }
      },
      [isFR]
    );

  useEffect(
    () => {
      loadScreenplays();
    },
    [loadScreenplays]
  );

  function openScreenplay(
    work
  ) {
    const destination =
      safeReaderDestination(
        work
          ?.reader
          ?.destination
      );

    if (!destination) {
      return;
    }

    window.location.href =
      destination;
  }

  return (
    <main
      className="ns-page"
      data-testid="screenplay-discovery"
      style={{
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <header
        className="ns-card"
        style={{
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing:
                  "0.06em",
                textTransform:
                  "uppercase",
                color: "#64748b",
              }}
            >
              NextScenes Reader
            </div>

            <h1
              className="ns-page-title"
            >
              {isFR
                ? "Scénarios"
                : "Screenplays"}
            </h1>

            <p
              className="ns-page-subtitle"
            >
              {isFR
                ? "Découvrez les scénarios diffusés et disponibles à la lecture sur NextScenes."
                : "Discover released screenplays available to read on NextScenes."}
            </p>
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={
              loadScreenplays
            }
            disabled={
              loading
            }
          >
            {loading
              ? (
                  isFR
                    ? "Chargement…"
                    : "Loading…"
                )
              : (
                  isFR
                    ? "Actualiser"
                    : "Refresh"
                )}
          </button>
        </div>
      </header>

      {error ? (
        <div
          className="ns-card"
          style={{
            marginBottom: 18,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading &&
      items.length === 0 ? (
        <div className="ns-card">
          {isFR
            ? "Chargement des scénarios…"
            : "Loading screenplays…"}
        </div>
      ) : null}

      {!loading &&
      !error &&
      items.length === 0 ? (
        <div className="ns-card">
          <h2
            style={{
              marginTop: 0,
            }}
          >
            {isFR
              ? "Aucun scénario diffusé pour le moment"
              : "No released screenplays yet"}
          </h2>

          <p
            className="ns-page-subtitle"
          >
            {isFR
              ? "Les scénarios rendus disponibles à la lecture apparaîtront ici."
              : "Screenplays made available for reading will appear here."}
          </p>
        </div>
      ) : null}

      {items.length ? (
        <section
          aria-label={
            isFR
              ? "Scénarios disponibles"
              : "Available Screenplays"
          }
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 18,
          }}
        >
          {items.map(
            (work) => {
              const workId =
                clean(
                  work?.workId
                );

              const title =
                clean(
                  work?.title
                ) ||
                (
                  isFR
                    ? "Scénario sans titre"
                    : "Untitled Screenplay"
                );

              const author =
                clean(
                  work
                    ?.creator
                    ?.name
                ) ||
                clean(
                  work
                    ?.creator
                    ?.username
                );

              const language =
                languageLabel(
                  work?.language
                );

              const destination =
                safeReaderDestination(
                  work
                    ?.reader
                    ?.destination
                );

              const visibility =
                visibilityLabel(
                  work?.visibility,
                  isFR
                );

              return (
                <article
                  key={
                    workId ||
                    destination ||
                    title
                  }
                  className="ns-card"
                  style={{
                    display: "grid",
                    alignContent:
                      "space-between",
                    gap: 18,
                    minHeight: 210,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing:
                          "0.06em",
                        textTransform:
                          "uppercase",
                        color: "#64748b",
                        marginBottom: 8,
                      }}
                    >
                      {isFR
                        ? "Scénario"
                        : "Screenplay"}
                    </div>

                    <h2
                      style={{
                        margin:
                          "0 0 10px",
                      }}
                    >
                      {title}
                    </h2>

                    {author ? (
                      <div
                        className="ns-card-meta"
                        style={{
                          marginBottom: 8,
                        }}
                      >
                        {isFR
                          ? "Par"
                          : "By"}{" "}
                        <strong>
                          {author}
                        </strong>
                      </div>
                    ) : null}

                    <div
                      className="ns-card-meta"
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {language ? (
                        <span>
                          {language}
                        </span>
                      ) : null}

                      <span>
                        {visibility}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    disabled={
                      !destination
                    }
                    onClick={() =>
                      openScreenplay(
                        work
                      )
                    }
                  >
                    {isFR
                      ? "Lire le scénario"
                      : "Read screenplay"}
                  </button>
                </article>
              );
            }
          )}
        </section>
      ) : null}
    </main>
  );
}

export {
  clean,
  safeReaderDestination,
  languageLabel,
  visibilityLabel,
};
